import { useEffect, useMemo, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";

import { reportError } from "../../../api/errors";
import {
  analyzeMembersImportCsv,
  commitMembersImportRows,
  type MemberImportCommitRowPayload,
  type MemberImportDetailRow,
  type MembersImportAnalyzeRow,
  type MembersImportCommitResult,
} from "../../../api/membersImport";
import { putMember } from "../../../api/members";
import { useAdminData } from "../AdminDataContext";

type OpBarState = {
  phase: "upload" | "processing";
  uploadPct: number;
  /** 逐条导入：总条数与已完成条数（用于整体进度条） */
  commitRowsTotal?: number;
  commitRowsDone?: number;
};

function ImportProgressPanel({
  title,
  state,
  uploadHint,
  processingHint,
  detail,
  stickyBar,
}: {
  title: string;
  state: OpBarState;
  uploadHint: string;
  processingHint: string;
  detail?: string;
  /** 逐条导入时吸顶，避免表格过长时看不到进度 */
  stickyBar?: boolean;
}) {
  const t = state.commitRowsTotal;
  const useCommitProgress = t != null && t > 0;
  const d = Math.max(0, state.commitRowsDone ?? 0);

  let barPct: number;
  let headerRight: ReactNode = null;

  if (useCommitProgress) {
    const totalRows = t;
    /* 仅按「已结束提交（收到响应）的条数 / 本次勾选总条数」，不把单条请求体上传百分比算进总进度 */
    const rawPct = totalRows <= 0 ? 0 : Math.min(100, Math.round((100 * d) / totalRows));
    barPct = rawPct;
    headerRight = (
      <span className="tabular-nums text-gray-600">
        {Math.min(d, totalRows)} / {totalRows}
      </span>
    );
  } else {
    const isUpload = state.phase === "upload";
    barPct = isUpload ? state.uploadPct : 0;
    headerRight = isUpload ? <span className="tabular-nums text-gray-600">{state.uploadPct}%</span> : null;
  }

  const isUploadLegacy = state.phase === "upload" && !useCommitProgress;
  const showSolidBar = useCommitProgress || isUploadLegacy;

  return (
    <div
      className={`rounded-xl border border-red-100 bg-red-50/60 px-4 py-3 space-y-2 ${stickyBar ? "sticky top-0 z-20 shadow-sm" : ""}`}
    >
      <div className="flex items-center justify-between gap-2 text-xs text-gray-700">
        <span className="font-medium text-gray-800">{title}</span>
        {headerRight}
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200/90">
        {showSolidBar ? (
          <div
            className="h-full rounded-full bg-red-500 transition-[width] duration-150 ease-out"
            style={{ width: `${Math.min(100, Math.max(0, barPct))}%` }}
          />
        ) : (
          <div className="h-full w-full rounded-full bg-red-400/80 animate-pulse" />
        )}
      </div>
      <p className="text-xs text-gray-600 flex items-center gap-2">
        <i className="ri-loader-4-line animate-spin shrink-0" />
        {state.phase === "upload" ? uploadHint : processingHint}
      </p>
      {useCommitProgress ? (
        <p className="text-xs text-gray-500 pl-6 tabular-nums">
          总进度：已提交 {Math.min(d, t)} / {t} 条
        </p>
      ) : null}
      {detail ? <p className="text-xs text-gray-500 pl-6">{detail}</p> : null}
    </div>
  );
}

type UiAnalyzeRow = MembersImportAnalyzeRow & { selected: boolean; importKey: string };

function newImportRowKey(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {
    /* noop */
  }
  return `import-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function digitsOnly(s: string): string {
  return s.replace(/\D/g, "");
}

function csvEscapeCell(s: string): string {
  const t = s ?? "";
  if (/[",\n\r]/.test(t)) return `"${t.replace(/"/g, '""')}"`;
  return t;
}

function appendRemark(prev: string, msg: string): string {
  const p = (prev ?? "").trim();
  const m = (msg ?? "").trim();
  if (!m) return p;
  return p ? `${p}；${m}` : m;
}

function isCommitImportSuccess(res: MembersImportCommitResult): boolean {
  if (!res || typeof res !== "object") return false;
  return (res as { ok?: unknown }).ok === true;
}

function firstCommitErrorMessage(res: MembersImportCommitResult): string {
  if (isCommitImportSuccess(res)) return "";
  const errs = (res as { ok?: false; errors?: { row: number; message: string }[] }).errors;
  if (Array.isArray(errs) && errs.length > 0) {
    const first = errs[0] as { message?: string; field?: string };
    if (first?.message) return first.message;
  }
  return "导入失败";
}

function buildUncheckedExportCsv(rows: UiAnalyzeRow[]): string {
  const unchecked = rows.filter((r) => !r.selected);
  const header = [
    "memberNo",
    "realName",
    "phone",
    "idNumber",
    "levelName",
    "city",
    "status",
    "initialPoints",
    "memberGroupName",
    "分组职位",
    "clothingSize",
    "工作单位",
    "政治面貌",
    "职业",
    "紧急联系人",
    "紧急联系电话",
    "会费金额",
    "parsedGender",
    "parsedBirthDate",
    "remark",
  ];
  const lines = [header.join(",")];
  for (const r of unchecked) {
    lines.push(
      [
        csvEscapeCell(r.memberNo),
        csvEscapeCell(r.realName),
        csvEscapeCell(r.phoneDigits),
        csvEscapeCell(r.idNumber),
        csvEscapeCell(r.levelName),
        csvEscapeCell(r.city),
        csvEscapeCell(r.status ?? ""),
        csvEscapeCell((r.initialPoints ?? "").trim()),
        csvEscapeCell(r.memberGroupName ?? ""),
        csvEscapeCell(r.memberGroupRoleName ?? ""),
        csvEscapeCell(r.clothingSize ?? ""),
        csvEscapeCell(r.organization ?? ""),
        csvEscapeCell(r.politicalStatus ?? ""),
        csvEscapeCell(r.occupation ?? ""),
        csvEscapeCell(r.emergencyContact ?? ""),
        csvEscapeCell(r.emergencyContactPhone ?? ""),
        csvEscapeCell(r.duesAmountYuan ?? ""),
        csvEscapeCell(r.parsedGender),
        csvEscapeCell(r.parsedBirthDate ?? ""),
        csvEscapeCell(r.remark),
      ].join(","),
    );
  }
  return "\uFEFF" + lines.join("\r\n");
}

export default function MemberImport() {
  const { tenantLegacyId, reload } = useAdminData();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<null | "analyze" | "commit" | "saveRow">(null);
  const [analyzeBar, setAnalyzeBar] = useState<OpBarState | null>(null);
  const [commitBar, setCommitBar] = useState<OpBarState | null>(null);
  const [commitBarDetail, setCommitBarDetail] = useState<string | null>(null);
  const [uiRows, setUiRows] = useState<UiAnalyzeRow[]>([]);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  /** 导入时是否调用身份证二要素实名接口（默认开启） */
  const [requireEidVerify, setRequireEidVerify] = useState(true);

  const [commitResult, setCommitResult] = useState<{
    ok: boolean;
    inserted: number;
    updated: number;
    applicationsUpserted?: number;
    welcomePointsPerNewMember?: number;
    errors?: unknown;
  } | null>(null);
  const [importDetails, setImportDetails] = useState<MemberImportDetailRow[]>([]);
  const [editRow, setEditRow] = useState<MemberImportDetailRow | null>(null);
  const [draft, setDraft] = useState({
    realName: "",
    phone: "",
    city: "",
    idNumber: "",
    levelName: "",
  });

  const selectedCount = useMemo(() => uiRows.filter((r) => r.selected).length, [uiRows]);
  const uncheckedCount = uiRows.length - selectedCount;

  const recommended = useMemo(
    () =>
      [
        "memberNo",
        "realName",
        "phone",
        "idNumber",
        "levelName",
        "city",
        "status",
        "initialPoints",
        "memberGroupName",
        "分组职位",
        "clothingSize",
        "工作单位",
        "政治面貌",
        "职业",
        "紧急联系人",
        "紧急联系电话",
        "会费金额",
      ].join(","),
    [],
  );

  useEffect(() => {
    if (!editRow) return;
    setDraft({
      realName: editRow.realName,
      phone: editRow.phone,
      city: editRow.city,
      idNumber: editRow.idNumber,
      levelName: editRow.levelName,
    });
  }, [editRow]);

  const resetSession = () => {
    setFile(null);
    setUiRows([]);
    setAnalyzeError(null);
    setCommitResult(null);
    setImportDetails([]);
    setEditRow(null);
    setAnalyzeBar(null);
    setCommitBar(null);
    setCommitBarDetail(null);
  };

  const onPickFile = async (f: File | null) => {
    setFile(f);
    setUiRows([]);
    setAnalyzeError(null);
    setCommitResult(null);
    setImportDetails([]);
    setEditRow(null);
    if (!f) return;
    setBusy("analyze");
    setAnalyzeBar({ phase: "upload", uploadPct: 0 });
    try {
      const res = await analyzeMembersImportCsv(tenantLegacyId, f, {
        onUploadProgress: (loaded, total) => {
          if (total > 0) setAnalyzeBar({ phase: "upload", uploadPct: Math.round((100 * loaded) / total) });
        },
        onUploadFinished: () => setAnalyzeBar({ phase: "processing", uploadPct: 100 }),
      });
      setUiRows(
        res.rows.map((r) => ({
          ...r,
          selected: r.defaultSelected,
          importKey: newImportRowKey(),
        })),
      );
    } catch (e) {
      setAnalyzeError("解析失败，请检查 CSV 编码与表头。");
      reportError(e, "解析失败");
    } finally {
      setAnalyzeBar(null);
      setBusy(null);
    }
  };

  const applyDefaultSelection = () => {
    setUiRows((prev) => prev.map((r) => ({ ...r, selected: r.defaultSelected })));
  };

  const clearSelection = () => {
    setUiRows((prev) => prev.map((r) => ({ ...r, selected: false })));
  };

  const exportUncheckedCsv = () => {
    if (uncheckedCount === 0) {
      window.alert("当前没有「未勾选」的行可导出。");
      return;
    }
    const blob = new Blob([buildUncheckedExportCsv(uiRows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `members-import-not-selected-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const doCommit = async () => {
    if (busy || selectedCount === 0) return;
    const selectedRows = uiRows.filter((r) => r.selected);
    const total = selectedRows.length;

    const ok = window.confirm(
      requireEidVerify
        ? `将逐条导入已勾选的 ${total} 条：每条会先经二要素与租户内查重；失败则本条写入备注、自动取消勾选并继续下一条。确认开始？`
        : `将逐条导入已勾选的 ${total} 条：已关闭二要素核验，仅做格式与租户内查重；失败则本条写入备注、自动取消勾选并继续下一条。确认开始？`,
    );
    if (!ok) return;

    setBusy("commit");
    flushSync(() => {
      setCommitBar({ phase: "upload", uploadPct: 0, commitRowsDone: 0, commitRowsTotal: total });
      setCommitBarDetail(null);
      setCommitResult(null);
      setImportDetails([]);
    });

    let totalInserted = 0;
    let totalUpdated = 0;
    let totalApps = 0;
    let welcomePts: number | undefined;
    let anyFailure = false;

    try {
      for (let n = 0; n < selectedRows.length; n++) {
        const row = selectedRows[n];
        const rowKey = row.importKey;
        flushSync(() => {
          setCommitBarDetail(`第 ${n + 1} / ${total} 条（行 ${row.row}）`);
          setCommitBar({
            phase: "upload",
            uploadPct: 0,
            commitRowsDone: n,
            commitRowsTotal: total,
          });
        });
        const one: MemberImportCommitRowPayload[] = [
          {
            memberNo: row.memberNoAutoGenerated ? "" : row.memberNo.trim(),
            realName: row.realName.trim(),
            phone: row.phoneDigits,
            idNumber: row.idNumber.trim(),
            levelName: row.levelName.trim(),
            city: row.city.trim(),
            status: (row.status ?? "").trim(),
            initialPoints: (row.initialPoints ?? "").trim(),
            memberGroupName: (row.memberGroupName ?? "").trim(),
            memberGroupRoleName: (row.memberGroupRoleName ?? "").trim(),
            clothingSize: (row.clothingSize ?? "").trim(),
            organization: (row.organization ?? "").trim(),
            politicalStatus: (row.politicalStatus ?? "").trim(),
            occupation: (row.occupation ?? "").trim(),
            emergencyContact: (row.emergencyContact ?? "").trim(),
            emergencyContactPhone: (row.emergencyContactPhone ?? "").trim(),
            duesAmountYuan: (row.duesAmountYuan ?? "").trim(),
          },
        ];
        const res = await commitMembersImportRows(tenantLegacyId, one, {
          requireEidVerify,
          onUploadProgress: (loaded, tot) => {
            if (tot > 0)
              setCommitBar((prev) =>
                prev
                  ? {
                      ...prev,
                      phase: "upload",
                      uploadPct: Math.round((100 * loaded) / tot),
                      commitRowsDone: n,
                      commitRowsTotal: total,
                    }
                  : null,
              );
          },
          onUploadFinished: () =>
            setCommitBar((prev) =>
              prev
                ? {
                    ...prev,
                    phase: "processing",
                    uploadPct: 100,
                    commitRowsDone: n,
                    commitRowsTotal: total,
                  }
                : null,
            ),
        });
        if (isCommitImportSuccess(res)) {
          const okBody = res as Extract<MembersImportCommitResult, { ok: true }>;
          totalInserted += okBody.inserted;
          totalUpdated += okBody.updated;
          totalApps += okBody.applicationsUpserted;
          if (okBody.welcomePointsPerNewMember != null) welcomePts = okBody.welcomePointsPerNewMember;
          setImportDetails((prev) => [...prev, ...(okBody.details ?? [])]);
          flushSync(() => {
            setUiRows((prev) => prev.filter((x) => x.importKey !== rowKey));
          });
        } else {
          anyFailure = true;
          const msg = firstCommitErrorMessage(res);
          flushSync(() => {
            setUiRows((prev) =>
              prev.map((x) =>
                x.importKey === rowKey ? { ...x, selected: false, remark: appendRemark(x.remark, msg) } : x,
              ),
            );
          });
        }
        setCommitBar((prev) =>
          prev
            ? {
                ...prev,
                commitRowsDone: n + 1,
                commitRowsTotal: total,
                phase: "processing",
                uploadPct: 100,
              }
            : null,
        );
      }

      setCommitBar({
        phase: "processing",
        uploadPct: 100,
        commitRowsDone: total,
        commitRowsTotal: total,
      });
      setCommitResult({
        ok: !anyFailure,
        inserted: totalInserted,
        updated: totalUpdated,
        applicationsUpserted: totalApps,
        welcomePointsPerNewMember: welcomePts,
        errors: anyFailure ? [{ row: 0, message: "部分条目未导入，请查看预检列表备注。" }] : undefined,
      });
      await reload();
    } catch (e) {
      reportError(e, "导入失败");
    } finally {
      setCommitBar(null);
      setCommitBarDetail(null);
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    if (!editRow || busy) return;
    const phoneDigits = digitsOnly(draft.phone);
    if (phoneDigits.length !== 11) {
      window.alert("手机号须为 11 位数字。");
      return;
    }
    const name = draft.realName.trim();
    if (!name) {
      window.alert("姓名不能为空。");
      return;
    }
    setBusy("saveRow");
    try {
      const saved = await putMember(tenantLegacyId, editRow.memberLegacyId, {
        realName: name,
        displayName: name,
        phone: phoneDigits,
        city: draft.city.trim(),
        idNumber: draft.idNumber.trim() || null,
        levelName: draft.levelName.trim() || undefined,
      });
      setImportDetails((prev) =>
        prev.map((r) =>
          r.memberLegacyId === editRow.memberLegacyId
            ? {
                ...r,
                realName: saved.realName,
                phone: saved.phone,
                city: saved.city,
                idNumber: draft.idNumber.trim(),
                levelName: saved.level,
                points: saved.points,
              }
            : r,
        ),
      );
      setEditRow(null);
      await reload();
    } catch (e) {
      reportError(e, "保存失败");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-bold text-gray-900">会员导入（CSV）</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                选择文件后仅做<strong>位数等基础预检</strong>（11 位手机、18 位身份证），不调用二要素；勾选后点「逐条确认导入」将<strong>逐条</strong>
                {requireEidVerify ? "二要素与" : ""}租户内查重再写入；性别与出生由合法身份证解析写入入会申请。可选列「memberGroupName」（会员分组名称；不存在时预检会自动写入分组字典）、「分组职位」（组内职位；不存在时自动补到该组；停用组/职会复用并重新启用）、「clothingSize」（衣服尺码，须为 S/M/L/XL/XXL/XXXL/XXXXL 或留空；无效值按未填写处理，写入时自动转大写）、「工作单位」「政治面貌」（非标准选项按原文写入）、「职业」「紧急联系人」「紧急联系电话」「会费金额」（元，非负整数；空按 0；写入会籍缴费时使用该金额，不取会员等级设置）。<strong>city</strong> 列若含完整省市区（县）地址，将自动拆分为居住地区与详细地址写入入会申请。
              </p>
            </div>
            <a
              className="text-xs text-gray-500 hover:text-red-600 transition-colors"
              href={`data:text/plain;charset=utf-8,${encodeURIComponent(recommended + "\n")}`}
              download="members-import-template.csv"
              title="下载 CSV 表头模板"
            >
              <i className="ri-download-line mr-1" />
              下载模板
            </a>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">导入时进行身份证二要素实名核验</p>
              <p className="text-xs text-gray-500 mt-0.5">
                开启后逐条导入时将调用实名接口校验姓名与身份证号；关闭后仅校验格式与租户内查重，适用于历史档案批量补录。
              </p>
            </div>
            <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 select-none">
              <span className="text-xs text-gray-500">{requireEidVerify ? "已开启" : "已关闭"}</span>
              <button
                type="button"
                role="switch"
                aria-checked={requireEidVerify}
                disabled={busy !== null}
                onClick={() => setRequireEidVerify((v) => !v)}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-40 ${
                  requireEidVerify ? "bg-red-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    requireEidVerify ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </label>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <i className="ri-file-excel-2-line text-green-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file ? file.name : "请选择 CSV（UTF-8 或 Excel 中文 ANSI）"}
                  </p>
                  <p className="text-xs text-gray-500">
                    表头：<span className="font-mono">{recommended}</span>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <label className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:border-red-300 hover:text-red-600 cursor-pointer transition-colors">
                  <i className="ri-upload-2-line" />
                  选择文件
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
                  />
                </label>
                <button
                  type="button"
                  disabled={uiRows.length === 0 || busy !== null}
                  onClick={applyDefaultSelection}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border border-gray-200 text-gray-700 hover:bg-white disabled:opacity-40"
                >
                  按预检默认勾选
                </button>
                <button
                  type="button"
                  disabled={uiRows.length === 0 || busy !== null}
                  onClick={clearSelection}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border border-gray-200 text-gray-700 hover:bg-white disabled:opacity-40"
                >
                  清空勾选
                </button>
                <button
                  type="button"
                  disabled={uncheckedCount === 0 || busy !== null}
                  onClick={exportUncheckedCsv}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm border border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-40"
                >
                  <i className="ri-file-download-line" />
                  导出未勾选 CSV
                </button>
                <button
                  type="button"
                  disabled={selectedCount === 0 || busy !== null}
                  onClick={() => void doCommit()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-40"
                >
                  <i className="ri-play-circle-line" />
                  {busy === "commit" ? "逐条导入中…" : `逐条确认导入（${selectedCount}）`}
                </button>
                {(uiRows.length > 0 || commitResult) && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={resetSession}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-gray-500 hover:text-gray-800 disabled:opacity-40"
                  >
                    重置
                  </button>
                )}
              </div>
            </div>
          </div>

          {busy === "analyze" && analyzeBar ? (
            <ImportProgressPanel
              title="文件预检"
              state={analyzeBar}
              uploadHint="正在上传 CSV 到服务器…"
              processingHint="正在解析并做位数预检，请稍候…"
            />
          ) : null}

          {busy === "commit" && commitBar ? (
            <ImportProgressPanel
              title="逐条确认导入"
              state={commitBar}
              uploadHint="正在提交本条数据…"
              processingHint={
                requireEidVerify
                  ? "正在二要素核验并写入会员与入会申请…"
                  : "正在写入会员与入会申请（未启用二要素）…"
              }
              detail={commitBarDetail ?? undefined}
              stickyBar
            />
          ) : null}

          {analyzeError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{analyzeError}</div>
          )}

          {uiRows.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-800">预检列表</p>
                <p className="text-xs text-gray-500">
                  已勾选 {selectedCount} / {uiRows.length}；未勾选 {uncheckedCount} 可导出
                </p>
              </div>
              <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="text-left font-medium px-2 py-2 w-10">
                        <span className="sr-only">导入</span>
                      </th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">行</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">会员号</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">姓名</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">手机</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">身份证</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">等级</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap min-w-[120px]">city/地址</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">居住地区</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap min-w-[100px]">详细地址</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">工作单位</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">政治面貌</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">职业</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">紧急联系人</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">紧急电话</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">状态</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">初始积分</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">会员分组</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">分组职位</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">衣服尺码</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">会费金额</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">性别(解析)</th>
                      <th className="text-left font-medium px-2 py-2 whitespace-nowrap">出生(解析)</th>
                      <th className="text-left font-medium px-2 py-2 min-w-[200px]">备注</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {uiRows.map((r, idx) => (
                      <tr key={r.importKey} className={r.selected ? "bg-white" : "bg-gray-50/60"}>
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={r.selected}
                            disabled={busy !== null}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setUiRows((prev) =>
                                prev.map((x, j) => (j === idx ? { ...x, selected: checked } : x)),
                              );
                            }}
                          />
                        </td>
                        <td className="px-2 py-2 text-gray-500">{r.row}</td>
                        <td className="px-2 py-2 font-mono text-gray-900">
                          {r.memberNo}
                          {r.memberNoAutoGenerated ? (
                            <span className="ml-1 text-[10px] text-blue-600 font-sans">预生成</span>
                          ) : null}
                        </td>
                        <td className="px-2 py-2">{r.realName}</td>
                        <td className="px-2 py-2 font-mono">{r.phoneDigits}</td>
                        <td className="px-2 py-2 font-mono text-gray-700">{r.idNumber || "—"}</td>
                        <td className="px-2 py-2">{r.levelName || "—"}</td>
                        <td className="px-2 py-2 max-w-[160px] truncate" title={r.city || undefined}>{r.city || "—"}</td>
                        <td className="px-2 py-2 max-w-[140px] truncate" title={r.addressRegion || undefined}>{r.addressRegion?.trim() || "—"}</td>
                        <td className="px-2 py-2 max-w-[140px] truncate" title={r.addressDetail || undefined}>{r.addressDetail?.trim() || "—"}</td>
                        <td className="px-2 py-2">{r.organization?.trim() || "—"}</td>
                        <td className="px-2 py-2">{r.politicalStatus?.trim() || "—"}</td>
                        <td className="px-2 py-2">{r.occupation?.trim() || "—"}</td>
                        <td className="px-2 py-2">{r.emergencyContact?.trim() || "—"}</td>
                        <td className="px-2 py-2 font-mono">{r.emergencyContactPhone?.trim() || "—"}</td>
                        <td className="px-2 py-2">
                          {r.statusLabel || (r.statusCode === 1 ? "过期会员" : r.statusCode === 0 ? "有效会员" : r.status || "有效会员")}
                        </td>
                        <td className="px-2 py-2 tabular-nums text-gray-700">
                          {r.initialPointsParsed != null && r.initialPointsParsed > 0
                            ? r.initialPointsParsed
                            : (r.initialPoints ?? "").trim() || "—"}
                        </td>
                        <td className="px-2 py-2">
                          {r.memberGroupResolvedName?.trim() ||
                            (r.memberGroupName ?? "").trim() ||
                            "—"}
                        </td>
                        <td className="px-2 py-2">
                          {r.memberGroupRoleResolvedName?.trim() ||
                            (r.memberGroupRoleName ?? "").trim() ||
                            "—"}
                        </td>
                        <td className="px-2 py-2 font-mono">
                          {r.clothingSizeNormalized?.trim() ||
                            (r.clothingSize ?? "").trim() ||
                            "—"}
                        </td>
                        <td className="px-2 py-2 tabular-nums text-gray-700">
                          {r.duesAmountYuanParsed != null
                            ? r.duesAmountYuanParsed
                            : (r.duesAmountYuan ?? "").trim() || "0"}
                        </td>
                        <td className="px-2 py-2">{r.parsedGender || "—"}</td>
                        <td className="px-2 py-2 font-mono">{r.parsedBirthDate || "—"}</td>
                        <td className="px-2 py-2 text-amber-900">{r.remark || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {commitResult && (
            <div className={`rounded-xl border px-4 py-3 ${commitResult.ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
              <p className={`text-sm font-semibold ${commitResult.ok ? "text-green-700" : "text-red-700"}`}>
                {commitResult.ok ? "导入成功" : "导入未完成"}
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                新增 {commitResult.inserted}，更新 {commitResult.updated}
                {commitResult.applicationsUpserted != null ? `；入会申请 ${commitResult.applicationsUpserted} 条` : ""}
                {commitResult.welcomePointsPerNewMember != null ? `；新会员 +${commitResult.welcomePointsPerNewMember} 分` : ""}。
              </p>
              {!commitResult.ok && Array.isArray(commitResult.errors) && commitResult.errors.length > 0 && (
                <ul className="mt-2 max-h-40 overflow-auto text-xs text-red-800 space-y-1">
                  {(commitResult.errors as { row: number; message: string }[]).slice(0, 80).map((e, idx) => (
                    <li key={`${e.row}-${idx}`}>
                      第 {e.row} 条：{e.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {importDetails.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-800">本次已导入明细</p>
                <span className="text-xs text-gray-500">可编辑校正（同步 import-* 申请）</span>
              </div>
              <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">行</th>
                      <th className="text-left font-medium px-3 py-2">操作</th>
                      <th className="text-left font-medium px-3 py-2">会员号</th>
                      <th className="text-left font-medium px-3 py-2">姓名</th>
                      <th className="text-left font-medium px-3 py-2">手机</th>
                      <th className="text-left font-medium px-3 py-2">证件</th>
                      <th className="text-left font-medium px-3 py-2">性别</th>
                      <th className="text-left font-medium px-3 py-2">出生</th>
                      <th className="text-left font-medium px-3 py-2">等级</th>
                      <th className="text-left font-medium px-3 py-2">分组</th>
                      <th className="text-left font-medium px-3 py-2">职位</th>
                      <th className="text-left font-medium px-3 py-2">尺码</th>
                      <th className="text-left font-medium px-3 py-2">会费</th>
                      <th className="text-left font-medium px-3 py-2">状态</th>
                      <th className="text-left font-medium px-3 py-2">积分</th>
                      <th className="text-right font-medium px-3 py-2"> </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {importDetails.map((r) => (
                      <tr key={`${r.memberLegacyId}-${r.row}`} className="hover:bg-gray-50/80">
                        <td className="px-3 py-2 text-gray-500">{r.row}</td>
                        <td className="px-3 py-2">
                          <span
                            className={
                              r.operation === "inserted" ? "text-emerald-700 font-medium" : "text-amber-800 font-medium"
                            }
                          >
                            {r.operation === "inserted" ? "新增" : "更新"}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono">{r.memberNo}</td>
                        <td className="px-3 py-2">{r.realName}</td>
                        <td className="px-3 py-2 font-mono">{r.phone}</td>
                        <td className="px-3 py-2 font-mono text-gray-600">{r.idNumber || "—"}</td>
                        <td className="px-3 py-2">{r.parsedGender || "—"}</td>
                        <td className="px-3 py-2 font-mono">{r.parsedBirthDate || "—"}</td>
                        <td className="px-3 py-2">{r.levelName}</td>
                        <td className="px-3 py-2">{r.memberGroupName?.trim() || "—"}</td>
                        <td className="px-3 py-2">{r.memberGroupRoleName?.trim() || "—"}</td>
                        <td className="px-3 py-2 font-mono">{r.clothingSize?.trim() || "—"}</td>
                        <td className="px-3 py-2 tabular-nums">{r.duesAmountYuan ?? 0}</td>
                        <td className="px-3 py-2">{r.statusLabel ?? (r.status === 1 ? "过期会员" : "有效会员")}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.points}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            disabled={busy !== null}
                            onClick={() => setEditRow(r)}
                            className="text-red-600 hover:text-red-700 font-medium disabled:opacity-40"
                          >
                            编辑
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
            <p className="text-xs font-semibold text-gray-700 mb-1.5">说明</p>
            <ul className="text-xs text-gray-600 space-y-1.5">
              <li>- 文件预检默认仅当「11 位手机 + 身份证 18 位」时自动勾选；其他问题写在备注，默认不勾选。</li>
              <li>- 逐条导入时{requireEidVerify ? "须完成二要素；未配置实名服务或核验/查重失败" : "已关闭二要素，仅格式与查重；失败"}会写入备注并跳过该条。</li>
              <li>- 租户内已存在相同会员号或身份证号的，不会导入；手机号允许与其他会员重复；与线上入会申请可并存（import-*）。</li>
              <li>- 状态列 <span className="font-mono">status</span>：留空或 <span className="font-mono">0</span> 为有效会员（已通过审核），<span className="font-mono">1</span> 为过期会员。</li>
              <li>- 会员初始积分列 <span className="font-mono">initialPoints</span>：留空或 <span className="font-mono">0</span> 不发放；填写正整数时，新导入会员将额外记入一条「会员导入初始积分」流水（与等级入会欢迎积分独立，规则不变）。</li>
              <li>- 会费金额列 <span className="font-mono">会费金额</span>：单位元，非负整数；留空按 <span className="font-mono">0</span>。有效会员导入时写入入会缴费记录与会籍快照，金额以本列为准，不取会员等级/年度等级字典中的会费。</li>
              <li>- <span className="font-mono">memberNo</span> 列可留空：将按租户「会员编号规则」（序号递增或随机尾段，含等级前缀）自动生成；预检列表会展示预生成编号，正式导入时占用序号。</li>
              <li>- <span className="font-mono">city</span> 列：可填简要城市（如「江西吉安」）或含省市区（县）的完整地址（如「江西省吉安市吉州区XX路1号」）；后者将自动拆分为入会申请的居住地区与详细地址。</li>
              <li>- 可选列「工作单位」「职业」「紧急联系人」「紧急联系电话」：留空不写入；职业写入入会扩展字段（f8）；格式问题仅备注提示，不阻断导入。</li>
            </ul>
          </div>
        </div>
      </div>

      {editRow && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-4 border border-gray-100">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-bold text-gray-900">编辑已导入会员</h4>
                <p className="text-xs text-gray-500 mt-0.5 font-mono">
                  {editRow.memberNo} · {editRow.memberLegacyId}
                </p>
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700 p-1"
                aria-label="关闭"
                onClick={() => !busy && setEditRow(null)}
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="text-xs text-gray-500">姓名</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                  value={draft.realName}
                  onChange={(e) => setDraft((d) => ({ ...d, realName: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">手机（11 位）</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 font-mono"
                  value={draft.phone}
                  onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">证件号</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 font-mono"
                  value={draft.idNumber}
                  onChange={(e) => setDraft((d) => ({ ...d, idNumber: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">等级</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                  value={draft.levelName}
                  onChange={(e) => setDraft((d) => ({ ...d, levelName: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">city / 地址</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                  value={draft.city}
                  onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => setEditRow(null)}
                className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                取消
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void saveEdit()}
                className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-500 disabled:opacity-40"
              >
                {busy === "saveRow" ? "保存中…" : "保存"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
